import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {
  GridOptions,
  GridReadyEvent,
  IGetRowsParams,
  GridSizeChangedEvent,
  ColDef,
  IDatasource,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  PaginationChangedEvent,
  FirstDataRenderedEvent,
  SortChangedEvent,
  CellStyle, RowSelectionOptions,
} from '@ag-grid-community/core';
import { takeUntil } from 'rxjs/operators';
import { TableActionButtonsComponent } from '../table-action-buttons/table-action-buttons.component';
import {
  ACTIONTYPES,
  AgGridConfig,
  agGridDefaultLocaleFr,
  IHandleServerDataSource,
  ITableStateUrlParams,
} from './models/table-actions-model';
import { TableAgGridRadioComponent } from './table-ag-grid-radio/table-ag-grid-radio.component';
import { IPaginate } from '@carm/_core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AG_GRID_LOCALE_EN, AG_GRID_LOCALE_FR } from './table-locale';
import { BasePageComponent } from '@carm/_utils/base-page.component';
import { TableAgHeaderActionsTooltipComponent } from './table-ag-header-actions-tooltip/table-ag-header-actions-tooltip.component';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'carm-ag-grid',
  templateUrl: './table-ag-grid.component.html',
  styleUrls: ['./table-ag-grid.component.scss'],
})
export class TableAgGridComponent
  extends BasePageComponent
  implements OnInit, OnChanges, OnDestroy {

  @Input() dataSource: any[];
  @Input() serverDataSource: IHandleServerDataSource;
  @Input() agGridConfig: AgGridConfig; // table configuration object provided by the parent component
  @Input() noContentBorder: boolean;
  @Input() noRowsMessage: any;
  @Input() enablePagination: boolean;
  @Input() paginationSize: number;
  @Input() limitedCache: boolean;
  @Input() itemPageSize: boolean;
  @Input() autoHeight = false;
  @Input() bottomPagePosition = false;
  @Input() inId = '';
  @Input() minPageSize: 100 | 500 | 1000 = 100;
  @Input() customServerSidePaging: IPaginate;
  @Output() pagingChanged = new EventEmitter<IPaginate>();
  @Output() radioSelection = new EventEmitter();
  @Output() gridReady = new EventEmitter<GridApi>();
  @Output() actionButtonClick = new EventEmitter(); // event informing that one of the action button was clicked. Contains data from the row and action id
  @Output() selectionChanged = new EventEmitter(); // event informing that selection using radio button changed. Contains array of data rows
  @Output() rowSelected = new EventEmitter(); // event informing which row has been selected

  public gridOptions: GridOptions;
  public localeText;
  public gridApi: GridApi;
  pageSizeGroup: UntypedFormGroup;
  public rowSelection: RowSelectionOptions

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private _el: ElementRef,
    private _renderer2: Renderer2,
    @Inject(LOCALE_ID) protected localeId: string,
    private zone: NgZone
  ) {
    super();
    if (this.noRowsMessage === undefined) {
      this.noRowsMessage = this.translateService.instant(
        'ag_grid_component.noRowsMessage'
      );
    }

    if (this.enablePagination === undefined) {
      this.enablePagination = true;
    }

    if (this.paginationSize === undefined) {
      this.paginationSize = 10;
    }

    if (this.itemPageSize === undefined) {
      this.itemPageSize = true;
    }
  }

  ngOnInit() {
    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntil(this.destroy$))
      .subscribe(_ => this.repaintGrid());

      this.pageSizeGroup = this.fb.group({
        sizeSelect: [],
      });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.agGridConfig &&
      changes.agGridConfig.currentValue !== changes.agGridConfig.previousValue
    ) {
      this.setupGridConfig(changes.agGridConfig.currentValue);
    }

    if (changes.dataSource &&  this.gridApi &&
      (changes.dataSource.currentValue !== changes.dataSource.previousValue)
    ) {
      this.gridApi.updateGridOptions({rowData: changes.dataSource.currentValue}); // client paging

      const tableUrlState = this.getPaginationUrlParamsSnapshot();
      if(tableUrlState.currentPage){
        this.gridApi.paginationGoToPage(Number(tableUrlState.currentPage));
      }

      //overide default with url query param
      if(tableUrlState.pageSize){
        this.gridApi.updateGridOptions({paginationPageSize: Number(tableUrlState.pageSize)});
        this.pageSizeGroup.get('sizeSelect').setValue(tableUrlState.pageSize);
      }

      if(tableUrlState.sort){

        const sortParams = tableUrlState.sort.split("_");
        const colId = sortParams[0];
        const colSortDirection = sortParams[1].toLowerCase();

        const columnStates = this.gridApi.getColumnState();
        const column = columnStates.find(c => c.colId === colId);
        column.sort = colSortDirection.toLowerCase() === "asc" ? 'asc' : 'desc';

        this.gridApi.applyColumnState( { state: columnStates });
      }
    }

    if (changes.serverDataSource?.currentValue &&  this.gridApi &&
      (changes.serverDataSource?.currentValue !== changes.serverDataSource?.previousValue)
    ) {
      if(this.agGridConfig.rowModelType === 'infinite'){
        const datasource = this.mapInfiniteDataSource(this.serverDataSource);
        this.gridApi.updateGridOptions({datasource: datasource}); // server paging
      }

      if(this.agGridConfig.rowModelType === 'serverSide'){
        const datasource = this.mapServerSideDataSource(this.serverDataSource);
        this.gridApi.updateGridOptions({serverSideDatasource: datasource}); // server paging
      }
    }
  }

  // Axe returning warning when horizontal scrollbar appears in grid and not keyboard accessible.
  // ag-grid already supports keyboard in cells so perhaps a false postive?
  private fixAccessibilityWarning() {
    const viewPortElement = this._el.nativeElement.querySelector(
      '.ag-center-cols-viewport'
    );
    this._renderer2.removeAttribute(viewPortElement, 'tabindex');
    this._renderer2.removeAttribute(viewPortElement, 'role');
  }

  private repaintGrid() {
    if (this.gridOptions && this.gridApi) {
      this.gridApi.setColumnsPinned(
        ['actions'],
        this.breakpointObserver.isMatched(Breakpoints.XSmall) ? null : 'right'
      );
    }
  }

  public onFilterChanged() {
    if (this.gridApi) {
      this.gridApi.onFilterChanged();
    }
  }

  private setupGridConfig(config: AgGridConfig) {
    // Adding the column with checkboxes if checkbox property in config is set to true

    this.localeText =
      this.localeId === 'fr' ? AG_GRID_LOCALE_FR : AG_GRID_LOCALE_EN;

    const isClientSideResults =
      !config.rowModelType || config.rowModelType === 'clientSide';

    if (this.agGridConfig.checkbox === true) {
      if (
        this.agGridConfig.columnDefs.filter(c => c.colId === 'cboxAll')
          .length === 0
      ) {
        this.agGridConfig.columnDefs.unshift({
          headerName: isClientSideResults
            ? this.translateService.instant('ag_grid_component.all')
            : '',
          colId: 'cboxAll',
          field: 'cbox',
          headerCheckboxSelection: isClientSideResults ? true : false, // "select all" only works for clientSide table
          headerCheckboxSelectionFilteredOnly: true,
          checkboxSelection: true,
          width: 112,
          minWidth: 112,
          maxWidth: 112,
          filter: false,
        });
      }
    }

    // Adding the column with radiobutton if radiobutton property in config is set to true
    if (this.agGridConfig.radiobutton === true) {
      this.agGridConfig.columnDefs.unshift({
        minWidth: 65,
        sortable: false,
        cellRenderer: TableAgGridRadioComponent,
        cellRendererParams: {
          onClick: this.onRadioSelection.bind(this),
        },
      });
    }

    // Adding the action column with button if config contains actions array
    if (this.agGridConfig.actions && this.agGridConfig.actions.length > 0) {
      let cellStyle: CellStyle;
      if (
        this.agGridConfig?.actions.some(
          b => b.actionType === ACTIONTYPES.select
        )
      ) {
        cellStyle = { paddingLeft: 0, margin: 'auto' };
      }

      // duplicate actions panel bug fix, if columnDefs already have Actions panel, do not push again
      const actionPanel = this.agGridConfig.columnDefs.find(
        col => col.colId === 'actions'
      );

      if (!actionPanel) {
        this.agGridConfig.columnDefs.push({
          headerName: this.translateService.instant(
            'ag_grid_component.actions.actions'
          ),
          headerComponent: this.agGridConfig.actionsHeaderData ? TableAgHeaderActionsTooltipComponent : undefined,
          headerComponentParams: this.agGridConfig.actionsHeaderData ? this.agGridConfig.actionsHeaderData : undefined,
          colId: 'actions',
          filter: false,
          cellRenderer: TableActionButtonsComponent,
          flex: 1,
          minWidth: this.calculateMaxColumnWidth(),
          maxWidth: this.calculateMaxColumnWidth(),
          cellStyle,
          pinned: this.breakpointObserver.isMatched(Breakpoints.XSmall)
            ? undefined
            : 'right',
          cellRendererParams: {
            actions: this.agGridConfig.actions,
            clicked: v => this.zone.run(() => this.actionButtonClick.emit(v)), // zone.run required to avoid IE zone issues.
          },
          sortable: false
        });
      }
    }

    // Setting grid options including column definitions and ag-grid eventHandlers
    this.gridOptions = {
      pagination: this.enablePagination && !this.customServerSidePaging,
      paginationPageSize: this.paginationSize,
      cacheBlockSize: (this.customServerSidePaging || this.agGridConfig.rowModelType === 'serverSide' || this.limitedCache ) ? this.paginationSize : null, // sets max per request to same as page size.  Default is 100 if not set
      animateRows: true,
      enableCellTextSelection: true,
      suppressCellFocus: false,
      columnDefs: this.agGridConfig.columnDefs,
      onRowSelected: this.onRowSelected,
      onSelectionChanged: this.onSelectionChanged,
      loading: false,
      paginationPageSizeSelector: false,
      localeText:
        this.translateService.currentLang === 'fr'
          ? agGridDefaultLocaleFr
          : undefined,
      onGridSizeChanged: this.onGridSizeChanged,
      enableBrowserTooltips: true,
      isExternalFilterPresent: this.agGridConfig.isExternalFilterPresent,
      doesExternalFilterPass: this.agGridConfig.doesExternalFilterPass,
      overlayNoRowsTemplate: `${this.noRowsMessage}`,
      rowModelType: this.agGridConfig.rowModelType || 'clientSide',
      onGridReady: this.onGridReady,
      onFirstDataRendered: this.onFirstDataRendered,
      domLayout: this.autoHeight ? 'autoHeight' : 'normal',
      suppressMovableColumns: true,
      masterDetail: this.agGridConfig.masterDetail,
      detailCellRendererParams: this.agGridConfig.detailCellRendererParams,
      accentedSort: true,
      rowHeight: 47
    };

    this.applyColumnDefaults(this.agGridConfig.columnDefs);

    this.rowSelection = {
      mode: "multiRow",
      enableClickSelection: false,
      checkboxes: false,
      headerCheckbox: false,
      isRowSelectable: this.agGridConfig.checkboxSelectable
    }
  }

  private applyColumnDefaults(cols: ColDef[]) {
    cols.forEach(col => {
      this.applyDefaultMinWidth(col);
    });
  }

  // set default min width, but only if no width has been set explicitly
  private applyDefaultMinWidth(col: ColDef) {
    if (!col.width && !col.maxWidth && !col.minWidth) {
      col.minWidth = 190;
    }
  }

  private calculateMaxColumnWidth(): number {
    const maxColumnWidth = 64;
    const maxColumnCount = 4;
    const paddingArea = 46;
    if (this.agGridConfig.maxActionsColumnWidth) {
      return (maxColumnWidth * this.agGridConfig.maxActionsColumnWidth) + paddingArea;
    }

    const actionsCount = this.agGridConfig.actions?.length > maxColumnCount ?
                         this.agGridConfig.actions?.length - 1 : this.agGridConfig.actions?.length;

    if (actionsCount === 1) {
      return 123;
    }

    const maxWidth =
      actionsCount >= maxColumnCount
        ? (maxColumnWidth * maxColumnCount) + paddingArea
        : (maxColumnWidth * actionsCount) + paddingArea;
    return maxWidth;
  }

  // Emits when selection in the table using checkboxes changes. Contains array of row data.
  onSelectionChanged = event => {
    const selectedRows = event.api.getSelectedNodes().map(item => item.data);
    this.selectionChanged.emit(selectedRows);
  };

  // Emits event when row is selected. Contains all item data.
  onRowSelected = event => {
    this.rowSelected.emit(event.data);
  };

  onGridReady = (event: GridReadyEvent) => {
    this.gridApi = event.api;

    if (this.dataSource) {
      this.gridApi.updateGridOptions({rowData: this.dataSource});
    }

    if (this.agGridConfig.rowModelType === 'infinite') {
      const datasource = this.mapInfiniteDataSource(this.serverDataSource);
      this.gridApi.updateGridOptions({datasource: datasource});
    }

    if (this.agGridConfig.rowModelType === 'serverSide') {
      const datasource = this.mapServerSideDataSource(this.serverDataSource);
      this.gridApi.updateGridOptions({serverSideDatasource: datasource});
    }

    this.gridReady.emit(this.gridApi);
  };


  onFirstDataRendered = (firstRowRenderedEvent: FirstDataRenderedEvent) => {
    this.fixAccessibilityWarning();

    this.resizeColumnsToFit();
  };

  onClientSortChanged(event: SortChangedEvent){
      const columnStates = this.gridApi.getColumnState().filter(c => c.sort);

      if(columnStates && columnStates.length > 0){

        const sortedColumn = columnStates[0];
        const tableUrlState = this.getPaginationUrlParamsSnapshot();

        const sortParam = this.serverDataSource?.sortByColumns?.find(k => k.key === sortedColumn.colId)?.value || sortedColumn.colId; // need to check both serverDatasource + clientDatasource sort param


        if(sortParam){
          tableUrlState.sort = `${this.inId}-${sortParam}_${sortedColumn.sort.toUpperCase()}`;
          tableUrlState.pageSize = tableUrlState.pageSize ? `${this.inId}-${tableUrlState.pageSize}` : undefined;
          tableUrlState.currentPage = tableUrlState.currentPage ? `${this.inId}-${tableUrlState.currentPage}` : undefined;
          this.persistTableStateInUrl(tableUrlState);
        }
      }
  }

  private mapInfiniteDataSource(infiniteDataSource: IHandleServerDataSource): IDatasource {
    const dataSource: IDatasource = {
      getRows: (params: IGetRowsParams) => {

        const pageSize = params.endRow - params.startRow;
        const pageIndex = params.startRow / pageSize + 1;

        const tableUrlState = this.getPaginationUrlParamsSnapshot();

        //overide default with url query param
        if(tableUrlState.pageSize){
          this.gridApi.updateGridOptions({paginationPageSize: Number(tableUrlState.pageSize)});
          this.pageSizeGroup.get('sizeSelect').setValue(tableUrlState.pageSize);
        }

        let sort = tableUrlState.sort || infiniteDataSource.defaultSortColumn;

        if (params.sortModel.length > 0) {
          const sortField = infiniteDataSource.sortByColumns.filter(
            x => x.key === params.sortModel[0].colId
          )[0].value;
          const direction = (params.sortModel[0].sort as string).toUpperCase();

          sort = `${sortField}_${direction}`;
        }

        this.gridApi.setGridOption("loading", false);
        infiniteDataSource
          .getRows(pageIndex, pageSize, sort)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            response => {

              const noResults = !response?.results || response.results.length === 0;
              if (noResults) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
              }
              const totalCount = noResults ? 0 : response.totalRecords;
              params.successCallback(response?.results || [], totalCount);

              //overide default with url query param
              if(tableUrlState.currentPage){
                this.gridApi.paginationGoToPage(Number(tableUrlState.currentPage));
              }


            },
            err => {
              this.gridApi.hideOverlay();
              this.gridApi.showNoRowsOverlay();
              infiniteDataSource.handleError(err);
              params.successCallback([], 0);
            }
          );
      },
    };

    return dataSource;
  }

  private mapServerSideDataSource(serverDataSource: IHandleServerDataSource): IServerSideDatasource {
    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams) => {

        let pageIndex: number = null;
        let pageSize: number = null;

        if(params.request?.startRow && params.request?.endRow){
          pageSize = params.request?.endRow - params.request?.startRow;
          pageIndex = params.request?.startRow / pageSize + 1;
        }

        const tableUrlState = this.getPaginationUrlParamsSnapshot();

        //overide default with url query param
        if(tableUrlState.pageSize){
          this.gridApi.updateGridOptions({paginationPageSize: Number(tableUrlState.pageSize)});
          this.pageSizeGroup.get('sizeSelect').setValue(tableUrlState.pageSize);
        }

        let sort = tableUrlState.sort || serverDataSource.defaultSortColumn;

        if (params.request.sortModel.length > 0) {
          const sortField = serverDataSource.sortByColumns.filter(
            x => x.key === params.request.sortModel[0].colId
          )[0].value;
          const direction = (params.request.sortModel[0].sort as string).toUpperCase();

          sort = `${sortField}_${direction}`;
        }

        this.gridApi.showLoadingOverlay();
        serverDataSource
          .getRows(pageIndex, pageSize, sort)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            response => {

              const noResults = !response?.results || response.results.length === 0;
              if (noResults) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
              }

              const loadParamsSuccess: LoadSuccessParams = {
                rowData: response?.results,
                rowCount: response.totalRecords || 0
              }
              params.success(loadParamsSuccess);

              //overide default with url query param
              if(tableUrlState.currentPage){
                this.gridApi.paginationGoToPage(Number(tableUrlState.currentPage));
              }


            },
            err => {
              this.gridApi.hideOverlay();
              this.gridApi.showNoRowsOverlay();
              serverDataSource.handleError(err);
              params.fail();
            }
          );
      },
    };

    return dataSource;
  }

  onGridSizeChanged(e: GridSizeChangedEvent) {
    if (e.clientWidth !== 0 && e.clientHeight !== 0){
      e.api.sizeColumnsToFit();
    }
  }

  // Generic fiter attached to the input above the table
  applyFilter(searchValue: string) {
    this.gridApi.updateGridOptions({quickFilterText: searchValue});
  }

  onPageSizeChanged(newPageSize: any) {
    const value = newPageSize.target.value;

    if (!!this.customServerSidePaging) {
      this.customServerSidePaging.page = 1;
      this.customServerSidePaging.count = +value;
      this.pagingChanged.emit(this.customServerSidePaging);
      return;
    }

    const newCurrentPage = 0; //reset to first page when pageSize changes

    this.gridApi.updateGridOptions({paginationPageSize: Number(value)});
    this.gridApi.paginationGoToPage(newCurrentPage);

     const tablestate: ITableStateUrlParams = {
        pageSize: `${this.inId}-${value}`,
        currentPage: `${this.inId}-${newCurrentPage}`
      };
      this.persistTableStateInUrl(tablestate);
    }

  onPreviousClicked(event: any, goToFirst: boolean = false): void {
    if (this.customServerSidePaging?.hasPrevious === false) {
      return;
    }
    if (goToFirst === true) {
      this.customServerSidePaging.page = 0;
      this.pagingChanged.emit(this.customServerSidePaging);
    } else {
      this.customServerSidePaging.page--;
      this.pagingChanged.emit(this.customServerSidePaging);
    }
  }

  onNextClicked(event: any, goToLast: boolean = false): void {
    if (this.customServerSidePaging?.hasNext === false) {
      return;
    }
    if (goToLast === true) {
      this.customServerSidePaging.page =
        this.customServerSidePaging.totalPages;
      this.pagingChanged.emit(this.customServerSidePaging);
    } else {
      this.customServerSidePaging.page = this.customServerSidePaging.page+1;
      this.pagingChanged.emit(this.customServerSidePaging);
    }
  }

  onRadioSelection(event: any) {
    this.radioSelection.emit(event.rowData);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public resizeColumnsToFit() {
    // this.gridApi.sizeColumnsToFit();

    if (this.agGridConfig.resizeAfterFirstRender) {
      // some grids have few columns but long content. need to resize after render.
      this.gridApi.autoSizeAllColumns();
    }
  }

  getPaginationUrlParamsSnapshot(): ITableStateUrlParams {

    const tableUrlState = this.activateRoute.snapshot.queryParams;

    let currentPageSizeUrlParams: string[] = null;
    let currentPageSizeTableId: string = null;
    let currentPageSize: string = null;

    if(tableUrlState.pageSize){
      currentPageSizeUrlParams = tableUrlState.pageSize.split('-');
      currentPageSizeTableId = currentPageSizeUrlParams[0];
      currentPageSize = currentPageSizeUrlParams[1];
    }

    let currentPageUrlParams: string[] = null;
    let currentPageTableId: string = null;
    let currentPage: string = null;

    if(tableUrlState.currentPage){
      currentPageUrlParams = tableUrlState.currentPage.split('-');
      currentPageTableId = currentPageUrlParams[0];
      currentPage = currentPageUrlParams[1];
    }

    let currentSortUrlParams: string[] = null;
    let currentSortTableId: string = null;
    let currentSort: string = null;

    if(tableUrlState.sort){
      currentSortUrlParams = tableUrlState.sort.split('-');
      currentSortTableId = currentSortUrlParams[0];
      currentSort = currentSortUrlParams[1];
    }

    return {
      pageSize: currentPageSizeTableId === this.inId ? currentPageSize : undefined,
      currentPage: currentPageTableId === this.inId ? currentPage : undefined,
      sort: currentSortTableId === this.inId ? currentSort : undefined,
    };
  }

  persistTableStateInUrl(tableState: ITableStateUrlParams) {
    this.router.navigate([], {
      relativeTo: this.activateRoute,
      queryParams: tableState,
      queryParamsHandling: 'merge',
      fragment: this.inId,
      replaceUrl: true,
    });
  }

  onPaginationChanged(event: PaginationChangedEvent) {
    if(event.newPage){
      if (this.gridApi) {
        const paginationObject: ITableStateUrlParams = {
          pageSize: `${this.inId}-${this.gridApi.paginationGetPageSize()}`,
          currentPage: `${this.inId}-${this.gridApi.paginationGetCurrentPage()}`
        };

        this.persistTableStateInUrl(paginationObject);
      }
    }



    const allRows = this.gridApi.getRenderedNodes(); // Get all rows currently rendered in the grid
    allRows.forEach((rowNode: any) => {
      // 'selected' property not always present
      if (rowNode.data?.selected) {
        rowNode.setSelected(rowNode.data?.selected);
      }
    });
  }
}
